import { axiosWI } from "./axios";
import {
  buildAnalysisGridParamsFromPayoutBootstrap,
  buildPayoutAnalysisQueryParams,
  hasMeaningfulGridParams,
  hasPayoutAnalysisChartLines,
  isDegeneratePayoutAnalysisData,
} from "helpers/payoutAnalysisApi";
import { isShareExpired } from "helpers/payoutAnalysisShareHelper";

const SHARE_ENDPOINT = "/payout-analysis-shares";
const SHARE_TOKEN_PATTERN = /^[0-9]{6}$/;

export function isValidShareToken(token) {
  return SHARE_TOKEN_PATTERN.test(token ?? "");
}

function throwShareError(result) {
  const error = new Error(result.state);
  error.shareState = result.state;
  throw error;
}

/**
 * Public payout analysis snapshot (no auth).
 * Supports the same query params as the authenticated analysis API.
 */
export async function loadSharedPayoutAnalysis(token, options = {}) {
  if (!isValidShareToken(token)) {
    return { state: "invalid_route" };
  }

  try {
    const { popup_session: _popupSession, ...shareOptions } = options;
    const params = buildPayoutAnalysisQueryParams(shareOptions);
    const response = await axiosWI.get(`${SHARE_ENDPOINT}/${token}`, {
      params,
      headers: { Accept: "application/json" },
      validateStatus: () => true,
    });

    if (response.status === 410) {
      return { state: "expired" };
    }

    if (response.status === 429) {
      return { state: "rate_limited" };
    }

    if (!response.status || response.status < 200 || response.status >= 300) {
      return { state: "error" };
    }

    const data = response.data?.data;
    if (isShareExpired(data?.share)) {
      return { state: "expired" };
    }

    return { state: "ready", data };
  } catch {
    return { state: "error" };
  }
}

export async function fetchSharedPayoutAnalysisResolved(token, options = {}) {
  const result = await loadSharedPayoutAnalysis(token, options);
  if (result.state === "ready") {
    return result.data;
  }
  throwShareError(result);
}

/**
 * Public payout calculator for a share token (no auth).
 * Mirrors authenticated `POST /option-rows/{id}/payout/calculate`.
 */
export async function calculateSharedPayout(token, options = {}) {
  if (!isValidShareToken(token)) {
    throwShareError({ state: "invalid_route" });
  }

  const { ul_settlement_price, side } = options;
  const params = {};
  if (side != null && String(side).trim() !== "") {
    params.side = side;
  }

  try {
    const response = await axiosWI.post(
      `${SHARE_ENDPOINT}/${token}/payout/calculate`,
      { ul_settlement_price },
      {
        params,
        headers: { Accept: "application/json" },
        validateStatus: () => true,
      }
    );

    if (response.status === 410) {
      throwShareError({ state: "expired" });
    }

    if (response.status === 429) {
      throwShareError({ state: "rate_limited" });
    }

    if (!response.status || response.status < 200 || response.status >= 300) {
      throwShareError({ state: "error" });
    }

    return response.data?.data ?? {};
  } catch (error) {
    if (error.shareState) {
      throw error;
    }
    throwShareError({ state: "error" });
  }
}

function gridParamsMatch(left = {}, right = {}) {
  return (
    Number(left.chart_increment ?? 0) === Number(right.chart_increment ?? 0) &&
    Number(left.starting_ul_settlement_price ?? NaN) ===
      Number(right.starting_ul_settlement_price ?? NaN)
  );
}

/**
 * Initial share load: fetch snapshot, then apply grid params in one follow-up
 * request when the backend needs them (avoids duplicate parallel queries).
 */
export async function fetchSharedPayoutAnalysisInitial(token, options = {}) {
  const baseParams = buildPayoutAnalysisQueryParams({
    metric: "P&L",
    days_elapsed: 0,
    ...options,
  });
  const data = await fetchSharedPayoutAnalysisResolved(token, baseParams);
  const gridParams = buildAnalysisGridParamsFromPayoutBootstrap(data, {
    chart_increment: options.chart_increment,
    starting_ul_settlement_price: options.starting_ul_settlement_price,
  });

  if (!hasMeaningfulGridParams(gridParams)) {
    return data;
  }

  if (
    hasPayoutAnalysisChartLines(data) &&
    !isDegeneratePayoutAnalysisData(data)
  ) {
    return data;
  }

  const gridRequestParams = buildPayoutAnalysisQueryParams({
    ...baseParams,
    ...gridParams,
  });

  if (gridParamsMatch(baseParams, gridRequestParams)) {
    return data;
  }

  return fetchSharedPayoutAnalysisResolved(token, gridRequestParams);
}
